import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { PleegKeuze, type PleegKeus } from "./PleegKeuze";
import { useCustom, useTranslate } from "@refinedev/core";

import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { metNummer } from "./AnimalOverview";
import { FilterInklapKnop, useFilterInklap } from "./FilterInklap";
import { IndeelbareTabel } from "./IndeelbareTabel";
import {
  DateField,
  PawIcon,
  ShieldIcon,
  ClockIcon,
  CalendarIcon,
  HomeIcon,
  MicrochipIcon,
  IdCardIcon,
  PaletteIcon,
  CombIcon,
  VenusIcon,
  CheckIcon,
  PinIcon,
  FileTextIcon,
  GearIcon,
  PencilIcon,
} from "@fawkes/ui";

// ── wire types (GET /api/entingen-overzicht | /api/ontwormen-overzicht) ────────────────────────────
// One shared aggregate behind both ANIMAL-scoped status overviews (faithful port of the Jmix
// EntingenOverzichtService / OntwormenOverzichtService `bouwOverzicht()` payload — see api
// OverzichtResponse). For the ontwormen overview the `entingstype` / `laatsteEntingDatum` /
// `vervaldatum*` keys carry the deworming meaning (treatment type / last treatment / next-due), exactly
// as the Jmix service reused the same keys.
export interface OverzichtCard {
  dierId: number;
  // The displayed (driver / nieuwste) inenting record id — the "Bewerken" deep-link target (opens the
  // Bescherming tile's edit dialog on this record). Null when the animal has no record yet (ONTBREEKT).
  inentingId: number | null;
  verblijfNummer: string | null;
  registratieNummer: number | null;
  naam: string | null;
  ras: string | null;
  diersoort: string | null;
  leeftijd: string | null;
  geboortedatum: string | null;
  geslacht: string | null;
  gecastreerd: string | null;
  castratiedatum: string | null;
  kleur: string | null;
  vacht: string | null;
  paspoortnummer: string | null;
  bijzonderheden: string | null;
  herkomst: string | null;
  opnamedatum: string | null;
  kennel: string | null;
  chipnummer: string | null;
  fotoUrl: string | null;
  status: string;
  entingstype: string | null;
  laatsteEntingDatum: string | null;
  vervaldatum: string | null;
  vervaldatumIso: string | null;
  vervalDagen: number | null;
  pleeggezin: boolean;
}
interface OverzichtResponse {
  kpi: { verlopen: number; binnenkort: number; ontbreekt: number; ok: number; totaal: number };
  cards: OverzichtCard[];
  filters: { diersoorten: string[]; entingstypes: string[] };
}

type Status = "VERLOPEN" | "BINNENKORT" | "ONTBREEKT" | "OK";
const ALL_STATUSES: Status[] = ["VERLOPEN", "BINNENKORT", "ONTBREEKT", "OK"];
// The source opens with OK hidden (only the three that need attention) and "Filters wissen" returns to
// this same set — so the screen opens on a different result set than "show everything" did.
const DEFAULT_STATUSES: Status[] = ["VERLOPEN", "BINNENKORT", "ONTBREEKT"];
const SORT_OPTIONS = [
  "urgent", "name", "nameDesc", "due", "species", "youngest", "oldestAge", "intakeNewest", "intakeOldest", "kennel",
] as const;
type Sort = (typeof SORT_OPTIONS)[number];

/** "dd-MM-yyyy" (zoals de kaarten datums tonen) → "yyyy-MM-dd", zodat datums als tekst te vergelijken zijn. */
function isoVanDmy(dmy: string | null): string | null {
  const m = dmy ? /^(\d{2})-(\d{2})-(\d{4})/.exec(dmy.trim()) : null;
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

/** Tekst/datum-vergelijking met lege waarden altijd achteraan, ongeacht de richting. */
function vergelijkLeegAchteraan(a: string | null, b: string | null, richting: 1 | -1): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, "nl", { numeric: true }) * richting;
}

// Jmix urgency ordering: most-urgent first.
const URGENCY: Record<string, number> = { VERLOPEN: 0, BINNENKORT: 1, ONTBREEKT: 2, OK: 3 };
// Maps each status to a tone class (host CSS): verlopen=danger/red, binnenkort=warning,
// ontbreekt=neutral/grey, ok=success. (Jmix used rood/oranje/geel/groen.)
// Screen-specific "VERLOPEN"/header glyph (source parity: entingen = 💉 syringe, ontwormen = 🪱 worm —
// distinct per screen instead of the shared shield). Kept local: these domain glyphs aren't shared UI.
function SyringeIcon({ width = 20, height = 20 }: { width?: number; height?: number }) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m18 2 4 4" />
      <path d="m17 7 3-3" />
      <path d="M19 9 8.7 19.3c-1 1-2.5 1-3.4 0l-.6-.6c-1-1-1-2.5 0-3.4L15 5" />
      <path d="m9 11 4 4" />
      <path d="m5 19-3 3" />
      <path d="m14 4 6 6" />
    </svg>
  );
}

function WormIcon({ width = 20, height = 20 }: { width?: number; height?: number }) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M7 21c-2 0-3-1.6-3-3.5S5.6 14 7.6 14 11 15.4 11 13s-2.6-3-2.6-5.6S10.6 3 13.6 3 18 4.8 18 7.4c0 2.1-1.6 2.7-1.6 4.6" />
      <circle cx="16.6" cy="6" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Status badge / KPI / pill colour (source parity `STATUS_META.kleur`): VERLOPEN=rood, BINNENKORT=
// oranje, ONTBREEKT=geel, OK=groen. "caution" is the geel tone (distinct from warning=oranje).
const STATUS_TONE: Record<string, string> = {
  VERLOPEN: "danger",
  BINNENKORT: "warning",
  ONTBREEKT: "caution",
  OK: "success",
};
// Expiry-text colour (source parity `vervalKleur`): rood / oranje / groen — and ONTBREEKT falls back
// to grijs (neutral), NOT geel. So the vervaldatum tone differs from the badge tone for ONTBREEKT.
const VERVAL_TONE: Record<string, string> = {
  VERLOPEN: "danger",
  BINNENKORT: "warning",
  ONTBREEKT: "neutral",
  OK: "success",
};
const PER_PAGE = 20;

/**
 * The Entingsstatus/Behandelstatus multiselect (source `StatusFilter`): a button that reads
 * "Geen" / "Alle" / "N geselecteerd", opening a checkbox panel with a coloured dot per status. Sits
 * beside the KPI tiles as a second way to steer the status filter.
 */
function StatusMultiSelect({
  selected,
  onToggle,
  label,
  statusLabel,
}: {
  selected: Status[];
  onToggle: (s: Status) => void;
  label: (selected: Status[]) => string;
  statusLabel: (s: Status) => string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const outside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);
  return (
    <div className="status-msel" ref={ref}>
      <button type="button" className="status-msel__btn status-overview__select" onClick={() => setOpen(!open)}>
        <span>{label(selected)}</span>
        <span className="status-msel__caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="status-msel__panel" role="listbox">
          {ALL_STATUSES.map((s) => (
            <label key={s} className="status-msel__item">
              <input type="checkbox" checked={selected.includes(s)} onChange={() => onToggle(s)} />
              <span className={`status-msel__dot status-msel__dot--${STATUS_TONE[s] ?? "neutral"}`} />
              {statusLabel(s)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Which i18n labels + the `type` filter param name differ between the two overviews. */
export interface StatusOverviewConfig {
  /** localStorage/prefs + page identity ("entingen" | "ontwormen"). */
  variant: "entingen" | "ontwormen";
  /** Relative useCustom url (dataProvider prepends /api): "entingen-overzicht" | "ontwormen-overzicht". */
  endpoint: string;
  /** The treatment-type query param: "entingstype" (entingen) | "type" (ontwormen). */
  typeParam: string;
  /** i18n key prefix, e.g. "entingenOverzicht" / "ontwormenOverzicht". */
  i18nPrefix: string;
}

export interface StatusOverviewProps {
  config: StatusOverviewConfig;
  /**
   * Open the animal detail. "Details" calls it with the dier id only; "Bewerken" additionally passes
   * the displayed record's inenting id so the detail opens its Bescherming tile's edit dialog on that
   * record (the mode — enting vs parasiet — is derived from the record by the tile itself).
   */
  onOpenAnimal: (dierId: string, inentingId?: number | null) => void;
}

const FIELD_ICON: Record<string, ReactNode> = {
  diersoort: <PawIcon width={15} height={15} />,
  kleur: <PaletteIcon width={15} height={15} />,
  vacht: <CombIcon width={15} height={15} />,
  leeftijd: <CalendarIcon width={15} height={15} />,
  geboortedatum: <CalendarIcon width={15} height={15} />,
  geslacht: <VenusIcon width={15} height={15} />,
  gecastreerd: <CheckIcon width={15} height={15} />,
  castratiedatum: <CalendarIcon width={15} height={15} />,
  herkomst: <PinIcon width={15} height={15} />,
  opnamedatum: <CalendarIcon width={15} height={15} />,
  kennel: <HomeIcon width={15} height={15} />,
  paspoortnummer: <IdCardIcon width={15} height={15} />,
  bijzonderheden: <FileTextIcon width={15} height={15} />,
  entingstype: <ShieldIcon width={15} height={15} />,
  laatsteEnting: <CalendarIcon width={15} height={15} />,
  vervaldatum: <ClockIcon width={15} height={15} />,
  chipnummer: <MicrochipIcon width={15} height={15} />,
};

// The selectable card-meta-rows / list-columns — a faithful port of the source `FIELDS` list (same 17
// keys in the same order). `verval` marks the expiry field, which is coloured by status. The user picks
// which are shown through the field chooser; `STANDARD_ON` is the source `STANDAARD_AAN` default set.
// `get` returns the raw display string; the expiry field is rendered via the component's `vervalText`
// (it needs i18n), so its `get` here is only a placeholder that the render branch ignores.
const FIELDS: Array<{ key: string; get: (c: OverzichtCard) => string | null; verval?: boolean }> = [
  { key: "diersoort", get: (c) => c.diersoort },
  { key: "kleur", get: (c) => c.kleur },
  { key: "vacht", get: (c) => c.vacht },
  { key: "leeftijd", get: (c) => c.leeftijd },
  { key: "geboortedatum", get: (c) => c.geboortedatum },
  { key: "geslacht", get: (c) => c.geslacht },
  { key: "gecastreerd", get: (c) => c.gecastreerd },
  { key: "castratiedatum", get: (c) => c.castratiedatum },
  { key: "herkomst", get: (c) => c.herkomst },
  { key: "opnamedatum", get: (c) => c.opnamedatum },
  { key: "kennel", get: (c) => c.kennel },
  { key: "paspoortnummer", get: (c) => c.paspoortnummer },
  { key: "bijzonderheden", get: (c) => c.bijzonderheden },
  { key: "entingstype", get: (c) => c.entingstype },
  { key: "laatsteEnting", get: (c) => c.laatsteEntingDatum },
  { key: "vervaldatum", get: () => null, verval: true },
  { key: "chipnummer", get: (c) => c.chipnummer },
];
const STANDARD_ON = new Set(["leeftijd", "kennel", "entingstype", "laatsteEnting", "vervaldatum"]);
type VeldenState = Record<string, boolean>;
const VELDEN_DEFAULT: VeldenState = Object.fromEntries(FIELDS.map((f) => [f.key, STANDARD_ON.has(f.key)]));
type Weergave = "grid" | "compact" | "lijst";

/**
 * The field chooser (source `VeldenChooser`): a "Velden" button that opens a checkbox panel over all
 * 17 selectable fields. Edits are staged in a draft and only applied on "Opslaan" — "Annuleren" or an
 * outside click discards them, exactly like the source (and the Dier-aanwezig field chooser).
 */
function VeldenChooser({
  velden,
  onChange,
  fieldLabel,
  openLabel,
  titleLabel,
  cancelLabel,
  saveLabel,
}: {
  velden: VeldenState;
  onChange: (v: VeldenState) => void;
  fieldLabel: (key: string) => string;
  openLabel: string;
  titleLabel: string;
  cancelLabel: string;
  saveLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<VeldenState>(velden);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const outside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);
  const openPanel = () => {
    setDraft(velden);
    setOpen(true);
  };
  const save = () => {
    onChange(draft);
    setOpen(false);
  };
  return (
    <div className="status-veldkiezer" ref={ref}>
      <button
        type="button"
        className="status-veldkiezer__btn"
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        <GearIcon width={15} height={15} /> {openLabel}
      </button>
      {open && (
        <div className="status-veldkiezer__panel">
          <div className="status-veldkiezer__title">{titleLabel}</div>
          <div className="status-veldkiezer__list">
            {FIELDS.map((f) => (
              <label key={f.key} className="status-veldkiezer__row">
                <input
                  type="checkbox"
                  checked={!!draft[f.key]}
                  onChange={() => setDraft({ ...draft, [f.key]: !draft[f.key] })}
                />
                <span className="status-veldkiezer__label">{fieldLabel(f.key)}</span>
              </label>
            ))}
          </div>
          <div className="status-veldkiezer__foot">
            <button type="button" className="status-veldkiezer__cancel" onClick={() => setOpen(false)}>
              {cancelLabel}
            </button>
            <button type="button" className="status-veldkiezer__save" onClick={save}>
              {saveLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Map a species name to its stat-card illustration (paw fallback), mirroring the Dier-aanwezig overview. */
function speciesIconSrc(naam: string | null): string {
  const n = (naam ?? "").toLowerCase();
  let file = "overig";
  if (n.includes("hond")) file = "hond";
  else if (n.includes("kat")) file = "kat";
  else if (n.includes("konijn")) file = "konijn";
  else if (n.includes("vogel")) file = "vogel";
  else if (n.includes("knaag")) file = "knaagdier";
  return `/species/${file}.png`;
}

export function StatusOverview({ config, onOpenAnimal }: StatusOverviewProps) {
  const t = useTranslate();
  const k = (suffix: string) => t(`${config.i18nPrefix}.${suffix}`);

  // Op een telefoon staat het filterblok dicht; alleen het zoekveld blijft staan. Zie
  // FilterInklap. Op een breed scherm is `toon` altijd waar en `mobiel` nooit.
  const { mobiel, open: filtersOpen, toon, wissel } = useFilterInklap();
  // Screen-specific glyph for the header + the VERLOPEN status (💉 entingen / 🪱 ontwormen).
  const PrimaryStatusIcon = config.variant === "ontwormen" ? WormIcon : SyringeIcon;

  // ── filter state ───────────────────────────────────────────────────────────────────────────────
  // diersoort + treatment-type are SERVER-side filters (the api takes them as query params); search +
  // status are client-side (status is driven by the clickable KPI tiles, like Jmix).
  const [search, setSearch] = useState("");
  const [diersoort, setDiersoort] = useState("");
  const [type, setType] = useState("");
  const [statuses, setStatuses] = useState<Status[]>([...DEFAULT_STATUSES]);
  // Vervaldatum (expiry) date-range — client-side, ISO yyyy-MM-dd lexicographic compare (Jmix parity).
  const [vanafDatum, setVanafDatum] = useState("");
  const [totDatum, setTotDatum] = useState("");
  // Wat er aanstaat, voor het getal op de inklapknop. De statusselectie telt alleen mee als hij
  // van de standaard afwijkt — anders zou de knop altijd een filter melden dat niemand zette.
  const statusAfwijkend = (gekozen: Status[]) =>
    gekozen.length !== DEFAULT_STATUSES.length || gekozen.some((x) => !DEFAULT_STATUSES.includes(x));
  const [sort, setSort] = useState<Sort>("urgent");
  // Foster animals are hidden by default (source parity); the toggle only affects the list, not the
  // KPI/stat counts (those always include foster).
  const [pleeg, setPleeg] = useState<PleegKeus>("geen");
  // Field chooser + view mode (source `velden` / `weergave`) — in-memory, resetting on reload exactly
  // like the source module (it does not persist these).
  const [velden, setVelden] = useState<VeldenState>(VELDEN_DEFAULT);
  const [weergave, setWeergave] = useState<Weergave>("grid");
  const [page, setPage] = useState(1);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (diersoort) p.set("diersoort", diersoort);
    if (type) p.set(config.typeParam, type);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [diersoort, type, config.typeParam]);

  const { data, isLoading, isError } = useCustom<OverzichtResponse>({
    url: `${config.endpoint}${query}`,
    method: "get",
  });
  const result = data?.data;
  const kpi = result?.kpi ?? { verlopen: 0, binnenkort: 0, ontbreekt: 0, ok: 0, totaal: 0 };
  const cards = result?.cards ?? [];
  const diersoorten = result?.filters?.diersoorten ?? [];
  const types = result?.filters?.entingstypes ?? [];

  // Per-species counts over the fetched set (Jmix `SoortKaarten`), most-common first. Clicking a card
  // sets the (server-side) diersoort filter; the Totaal card clears it. Filter-independent on the default
  // view, so all species are visible — the row Jmix shows above the KPIs.
  const soortTellingen = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cards) if (c.diersoort) m.set(c.diersoort, (m.get(c.diersoort) ?? 0) + 1);
    return [...m.entries()].map(([naam, aantal]) => ({ naam, aantal })).sort((a, b) => b.aantal - a.aantal);
  }, [cards]);
  // Source parity: the Totaal stat card counts ALL fetched animals (`dieren.length`), including those
  // without a species — NOT just the sum of the per-species buckets (which omits speciesless animals).
  const totaalDieren = cards.length;

  // Reset paging whenever any filter changes.
  useEffect(() => {
    setPage(1);
  }, [diersoort, type, search, statuses, vanafDatum, totDatum]);

  // ── client-side search + status filter + urgency sort ────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards
      .filter((c) => statuses.includes(c.status as Status))
      .filter((c) => (pleeg === "alleen" ? c.pleeggezin : pleeg === "beide" || !c.pleeggezin))
      .filter((c) => {
        if (!q) return true;
        const hay = `${c.naam ?? ""} ${c.chipnummer ?? ""} ${c.verblijfNummer ?? ""} ${c.kennel ?? ""}`.toLowerCase();
        return hay.includes(q);
      })
      .filter((c) => {
        // Vervaldatum range (Jmix): an active range excludes records with no expiry date.
        if (!vanafDatum && !totDatum) return true;
        if (!c.vervaldatumIso) return false;
        if (vanafDatum && c.vervaldatumIso < vanafDatum) return false;
        if (totDatum && c.vervaldatumIso > totDatum) return false;
        return true;
      })
      .sort((a, b) => {
        if (sort === "name") return (a.naam ?? "").localeCompare(b.naam ?? "");
        if (sort === "nameDesc") return (b.naam ?? "").localeCompare(a.naam ?? "");
        if (sort === "species") return (a.diersoort ?? "").localeCompare(b.diersoort ?? "");
        if (sort === "youngest") return vergelijkLeegAchteraan(isoVanDmy(a.geboortedatum), isoVanDmy(b.geboortedatum), -1);
        if (sort === "oldestAge") return vergelijkLeegAchteraan(isoVanDmy(a.geboortedatum), isoVanDmy(b.geboortedatum), 1);
        if (sort === "intakeNewest") return vergelijkLeegAchteraan(isoVanDmy(a.opnamedatum), isoVanDmy(b.opnamedatum), -1);
        if (sort === "intakeOldest") return vergelijkLeegAchteraan(isoVanDmy(a.opnamedatum), isoVanDmy(b.opnamedatum), 1);
        if (sort === "kennel") return vergelijkLeegAchteraan(a.kennel, b.kennel, 1);
        if (sort === "due") {
          const da = a.vervalDagen ?? Number.POSITIVE_INFINITY;
          const db = b.vervalDagen ?? Number.POSITIVE_INFINITY;
          return da - db;
        }
        // "urgent" (default): most-urgent status first, then soonest expiry.
        const u = (URGENCY[a.status] ?? 9) - (URGENCY[b.status] ?? 9);
        if (u !== 0) return u;
        const da = a.vervalDagen ?? Number.POSITIVE_INFINITY;
        const db = b.vervalDagen ?? Number.POSITIVE_INFINITY;
        return da - db;
      });
  }, [cards, statuses, search, vanafDatum, totDatum, sort, pleeg]);

  // Foster animals that pass the non-toggle filters — the toggle's "Toon N dieren in pleeggezin" count.
  const fosterCount = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards
      .filter((c) => c.pleeggezin)
      .filter((c) => statuses.includes(c.status as Status))
      .filter((c) => {
        if (!q) return true;
        const hay = `${c.naam ?? ""} ${c.chipnummer ?? ""} ${c.verblijfNummer ?? ""} ${c.kennel ?? ""}`.toLowerCase();
        return hay.includes(q);
      }).length;
  }, [cards, statuses, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * PER_PAGE;
  const visible = filtered.slice(start, start + PER_PAGE);

  // Load each visible animal's uploaded photo (authenticated blob → object URL). `fotoUrl` from the feed
  // is the online image (null for shelter-uploaded animals) / the Jmix-only /dier-foto path (404s here),
  // so cards fell back to the paw placeholder; this restores the real photo like acc.
  const photoUrls = useAnimalPhotoUrls(visible.map((c) => c.dierId));
  const photoOf = (c: OverzichtCard): string | null => photoUrls[String(c.dierId)] ?? c.fotoUrl;

  // KPI click: a single-status focus toggles back to "all" (Jmix toggleKpi); Totaal selects all.
  const toggleKpi = (s: Status | "TOTAAL") => {
    if (s === "TOTAAL") {
      setStatuses([...ALL_STATUSES]);
      return;
    }
    setStatuses(statuses.length === 1 && statuses[0] === s ? [...ALL_STATUSES] : [s]);
  };
  const isOnly = (s: Status) => statuses.length === 1 && statuses[0] === s;
  const isAll = statuses.length === ALL_STATUSES.length;

  const clearFilters = () => {
    setSearch("");
    setDiersoort("");
    setType("");
    setStatuses([...DEFAULT_STATUSES]);
    setVanafDatum("");
    setTotDatum("");
    setPleeg("geen");
  };

  const vervalText = (c: OverzichtCard) => {
    if (!c.vervaldatum) return "-";
    if (c.vervalDagen == null) return c.vervaldatum;
    const d = c.vervalDagen;
    // Source parity (`dagenTekst`): both signs render "%d dagen" with the SIGN kept (past = "-12 dagen",
    // future = "12 dagen"), and 0 = "vandaag". Not the reworded "N dagen geleden" / "in N dagen".
    const dagen = d === 0 ? k("card.today") : k("card.daysSigned").replace("%d", String(d));
    return `${c.vervaldatum} (${dagen})`;
  };

  return (
    <div className="status-overview">
      {/* Titel links, soortkaarten rechts — één regel, zoals de bron (`.kop`). */}
      <div className="status-overview__kop">
        {/* Header: icon + title + subtitle */}
        <header className="status-overview__header">
          <span className="status-overview__icon" aria-hidden="true">
            <PrimaryStatusIcon width={28} height={28} />
          </span>
          <div>
            <h1 className="status-overview__title">{k("title")}</h1>
            <p className="status-overview__subtitle">{k("subtitle")}</p>
          </div>
        </header>

        {/* Per-species stat cards (Jmix `SoortKaarten`) — clickable diersoort shortcut + Totaal. */}
        {soortTellingen.length > 0 && (
          <div className="da-overview__stats status-overview__stats">
            {soortTellingen.map((s) => (
              <button
                key={s.naam}
                type="button"
                className={`da-overview__stat${diersoort === s.naam ? " da-overview__stat--active" : ""}`}
                onClick={() => setDiersoort(diersoort === s.naam ? "" : s.naam)}
              >
                <span className="da-overview__stat-icon">
                  <img src={speciesIconSrc(s.naam)} alt="" />
                </span>
                <span className="da-overview__stat-body">
                  <span className="da-overview__stat-value">{s.aantal}</span>
                  <span className="da-overview__stat-label">{s.naam}</span>
                </span>
              </button>
            ))}
            <button
              type="button"
              className={`da-overview__stat${diersoort === "" ? " da-overview__stat--active" : ""}`}
              onClick={() => setDiersoort("")}
            >
              <span className="da-overview__stat-icon da-overview__stat-icon--total" aria-hidden="true">
                <PawIcon width={22} height={22} />
              </span>
              <span className="da-overview__stat-body">
                <span className="da-overview__stat-value">{totaalDieren}</span>
                <span className="da-overview__stat-label">{k("stat.total")}</span>
              </span>
            </button>
          </div>
        )}
      </div>

      {/* KPI tiles — clickable status filters (verlopen / binnenkort / ontbreekt / ok / totaal). */}
      <section className="status-overview__kpis" aria-label={k("kpi.aria")}>
        <KpiTile tone="danger" value={kpi.verlopen} label={k("kpi.verlopen")} active={isOnly("VERLOPEN")} onClick={() => toggleKpi("VERLOPEN")} icon={<PrimaryStatusIcon width={20} height={20} />} />
        <KpiTile tone="warning" value={kpi.binnenkort} label={k("kpi.binnenkort")} sub={k("kpi.binnenkortSub")} active={isOnly("BINNENKORT")} onClick={() => toggleKpi("BINNENKORT")} icon={<ClockIcon width={20} height={20} />} />
        <KpiTile tone="caution" value={kpi.ontbreekt} label={k("kpi.ontbreekt")} active={isOnly("ONTBREEKT")} onClick={() => toggleKpi("ONTBREEKT")} icon={<ShieldIcon width={20} height={20} />} />
        <KpiTile tone="success" value={kpi.ok} label={k("kpi.ok")} active={isOnly("OK")} onClick={() => toggleKpi("OK")} icon={<CheckIcon width={20} height={20} />} />
        <KpiTile tone="total" value={kpi.totaal} label={k("kpi.totaal")} active={isAll} onClick={() => toggleKpi("TOTAAL")} icon={<PawIcon width={20} height={20} />} />
      </section>

      {/* Filters: search + diersoort + treatment-type (both server-side) + clear. */}
      <section className="status-overview__filters">
        <div className="fawkes-products-search status-overview__search">
          {/* Source `.zoekIcon`: the 🔍 emoji, not a line icon. */}
          <span className="fawkes-products-search__icon" aria-hidden="true">🔍</span>
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={k("search.placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={k("search.placeholder")}
          />
        </div>
        {mobiel && (
          <FilterInklapKnop
            open={filtersOpen}
            actief={
              (diersoort ? 1 : 0) +
              (type ? 1 : 0) +
              (vanafDatum ? 1 : 0) +
              (totDatum ? 1 : 0) +
              (statusAfwijkend(statuses) ? 1 : 0)
            }
            label={t("filter.inklap")}
            onClick={wissel}
          />
        )}

        {toon && (
        <>
        <label className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.diersoort")}</span>
          <select className="status-overview__select" value={diersoort} onChange={(e) => setDiersoort(e.target.value)}>
            <option value="">{k("filter.all")}</option>
            {diersoorten.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <div className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.status")}</span>
          <StatusMultiSelect
            selected={statuses}
            onToggle={(s) =>
              setStatuses(statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s])
            }
            label={(sel) =>
              sel.length === 0
                ? k("filter.statusNone")
                : sel.length === ALL_STATUSES.length
                  ? k("filter.all")
                  : k("filter.statusN").replace("%d", String(sel.length))
            }
            statusLabel={(s) => k(`status.${s}`)}
          />
        </div>
        <label className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.type")}</span>
          <select className="status-overview__select" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">{k("filter.all")}</option>
            {types.map((ty) => (
              <option key={ty} value={ty}>{ty}</option>
            ))}
          </select>
        </label>
        <label className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.vervaldatumVan")}</span>
          <DateField className="status-overview__select"
            value={vanafDatum}
            onChange={(e) => setVanafDatum(e.target.value)}
          />
        </label>
        <label className="status-overview__field">
          <span className="status-overview__field-label">{k("filter.vervaldatumTot")}</span>
          <DateField className="status-overview__select"
            value={totDatum}
            onChange={(e) => setTotDatum(e.target.value)}
          />
        </label>
        <div className="status-overview__field status-overview__field--action">
          {/* Source `.wisBtn`: white pill + green-dark bold text + ↻ icon. */}
          <button type="button" className="da-overview__wis" onClick={clearFilters}>
            <span className="da-overview__wis-icon" aria-hidden="true">↻</span>
            {k("filter.clear")}
          </button>
        </div>
        {/* Foster toggle — its own row (source `.filterRij.tweede`). Hidden animals aren't counted in the
            KPIs; the toggle only reveals them in the list. */}
        <div className="status-overview__foster">
          <PleegKeuze waarde={pleeg} aantal={fosterCount} onKies={setPleeg} />
        </div>
        </>
        )}
      </section>


      {/* Results sub-bar */}
      <div className="status-overview__resultsbar">
        <span className="status-overview__results-count">
          <PawIcon width={16} height={16} /> {filtered.length}{" "}
          {filtered.length === 1 ? k("results.foundOne") : k("results.found")}
        </span>
        <div className="status-overview__resultsbar-right">
          <VeldenChooser
            velden={velden}
            onChange={setVelden}
            fieldLabel={(key) => k(`field.${key}`)}
            openLabel={k("veldkiezer.open")}
            titleLabel={k("veldkiezer.title")}
            cancelLabel={k("veldkiezer.cancel")}
            saveLabel={k("veldkiezer.save")}
          />
          <div className="status-overview__weergave" role="group" aria-label={k("veldkiezer.open")}>
            {(["grid", "compact", "lijst"] as const).map((w) => (
              <button
                key={w}
                type="button"
                className={weergave === w ? "is-active" : ""}
                aria-pressed={weergave === w}
                aria-label={k(`weergave.${w}`)}
                title={k(`weergave.${w}`)}
                onClick={() => setWeergave(w)}
              >
                <span aria-hidden="true">{w === "grid" ? "▦" : w === "compact" ? "▥" : "☰"}</span>
              </button>
            ))}
          </div>
          <label className="status-overview__sort">
            <span className="status-overview__sort-label">{k("sortLabel")}</span>
            <select
              className="da-overview__filter"
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
            >
              {SORT_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {k(`sort.${s}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{k("loadFailed")}</p>
      ) : visible.length === 0 ? (
        <p className="fawkes-muted">{k("empty")}</p>
      ) : weergave === "lijst" ? (
        <div className="status-overview__list">
          <IndeelbareTabel
            tabelKey={`status-overzicht-${config.variant}`}
            columns={[
              {
                key: "status", header: k("table.status"),
                cell: (c) => (
                  <span className={`status-list__pill status-list__pill--${STATUS_TONE[c.status] ?? "neutral"}`}>
                    {k(`status.${c.status}`)}
                  </span>
                ),
              },
              {
                key: "dier", header: k("table.dier"),
                cell: (c) => {
                  const foto = photoOf(c);
                  return (
                    <span className="status-list__dier">
                      {foto ? (
                        <img className="status-list__foto" src={foto} alt={c.naam ?? ""} />
                      ) : (
                        <span className="status-list__foto status-list__foto--empty" aria-hidden="true">
                          <PawIcon width={16} height={16} strokeWidth={1.5} />
                        </span>
                      )}
                      <span className="status-list__dier-text">
                        <b>
                          {metNummer(c.registratieNummer, c.naam)}
                          {c.pleeggezin ? <span className="status-list__foster-tag">🏠 {k("card.foster")}</span> : null}
                        </b>
                        <span>{c.ras || c.diersoort || "Onbekend ras"}</span>
                      </span>
                    </span>
                  );
                },
              },
              // Welke velden erbij staan kiest de gebruiker met de veldenkiezer; dat bleef zo.
              ...FIELDS.filter((f) => velden[f.key]).map((f) => ({
                key: f.key,
                header: k(`field.${f.key}`),
                cell: (c: OverzichtCard) => (f.verval ? (
                  <span className={`status-card__verval status-card__verval--${VERVAL_TONE[c.status] ?? "neutral"}`}>
                    {vervalText(c)}
                  </span>
                ) : (f.get(c) || "—")),
              })),
              {
                key: "acties", header: "", vast: true,
                cell: (c) => (
                  <span className="status-list__acties">
                    <button type="button" className="status-card__details"
                      onClick={() => onOpenAnimal(String(c.dierId))}>
                      {k("card.details")}
                    </button>
                    <button type="button" className="status-card__iconbtn"
                      title={k("card.bewerken")} aria-label={k("card.bewerken")}
                      onClick={() => onOpenAnimal(String(c.dierId), c.inentingId)}>
                      <PencilIcon width={16} height={16} />
                    </button>
                  </span>
                ),
              },
            ]}
            rows={visible}
            getRowKey={(c) => String(c.dierId)}
            rowClassName={(c) => (c.pleeggezin
              ? "status-list__row status-list__row--foster"
              : "status-list__row")}
            empty=""
          />
        </div>
      ) : (
        <div className={`status-overview__grid${weergave === "compact" ? " status-overview__grid--compact" : ""}`}>
          {visible.map((c) => (
            <DierStatusCard
              key={c.dierId}
              card={c}
              photoUrl={photoOf(c)}
              tone={STATUS_TONE[c.status] ?? "neutral"}
              vervalTone={VERVAL_TONE[c.status] ?? "neutral"}
              statusLabel={k(`status.${c.status}`)}
              velden={velden}
              vervalText={vervalText(c)}
              metaLabel={(key) => k(`field.${key}`)}
              chipLabel={k("card.chipLabel")}
              bewerkenTitle={k("card.bewerken")}
              fosterLabel={k("card.foster")}
              onDetails={() => onOpenAnimal(String(c.dierId))}
              // Bewerken: deep-link to the animal detail's Bescherming / vaccinaties tile with the
              // displayed record pre-selected, so its edit dialog opens on this exact inenting (the Jmix
              // "Bewerken" target). The dialog derives its mode (enting vs parasiet) from the record. When
              // there is no record yet (ONTBREEKT → inentingId null) it just opens the tile, no dialog.
              onBewerken={() => onOpenAnimal(String(c.dierId), c.inentingId)}
            />
          ))}
        </div>
      )}

      {/* Pagination (source `voet` footer): a range text — "{a} - {b} van {n} dieren" — plus a numbered
          pager (page 1, last page, and ±2 around the current page, "…" bridging the gaps) and a › next. */}
      <footer className="status-overview__pager">
        <span className="status-overview__pager-range">
          {filtered.length === 0
            ? k("pager.empty")
            : k("pager.range")
                .replace("%a", String(start + 1))
                .replace("%b", String(Math.min(start + PER_PAGE, filtered.length)))
                .replace("%n", String(filtered.length))}
        </span>
        {totalPages > 1 && (
          <div className="status-overview__pages">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - current) <= 2)
              .map((p, idx, arr) => (
                <Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                    <span className="status-overview__pager-dots">…</span>
                  )}
                  <button
                    type="button"
                    className={`status-overview__page${p === current ? " status-overview__page--active" : ""}`}
                    aria-current={p === current ? "page" : undefined}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                </Fragment>
              ))}
            <button
              type="button"
              className="status-overview__page status-overview__page--next"
              disabled={current >= totalPages}
              onClick={() => setPage(current + 1)}
            >
              ›
            </button>
          </div>
        )}
      </footer>
    </div>
  );
}

function KpiTile({
  tone,
  value,
  label,
  sub,
  active,
  onClick,
  icon,
}: {
  tone: string;
  value: number;
  label: string;
  sub?: string;
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`status-kpi status-kpi--${tone}${active ? " status-kpi--active" : ""}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="status-kpi__icon" aria-hidden="true">{icon}</span>
      <span className="status-kpi__body">
        <span className="status-kpi__value">{value}</span>
        <span className="status-kpi__label">{label}</span>
        {sub ? <span className="status-kpi__sub">{sub}</span> : null}
      </span>
    </button>
  );
}

function DierStatusCard({
  card,
  photoUrl,
  tone,
  vervalTone,
  statusLabel,
  velden,
  vervalText,
  metaLabel,
  chipLabel,
  bewerkenTitle,
  fosterLabel,
  onDetails,
  onBewerken,
}: {
  card: OverzichtCard;
  photoUrl?: string | null;
  tone: string;
  vervalTone: string;
  statusLabel: string;
  velden: VeldenState;
  vervalText: string;
  metaLabel: (key: string) => string;
  chipLabel: string;
  bewerkenTitle: string;
  fosterLabel: string;
  onDetails: () => void;
  onBewerken: () => void;
}) {
  const rows = FIELDS.filter((f) => velden[f.key]);
  // Source parity (`DierKaart`): the whole card is the click target that opens the animal — no separate
  // "Details" button on the card (that only lives on the list row).
  return (
    <div
      className={card.pleeggezin ? "status-card status-card--foster" : "status-card"}
      role="button"
      tabIndex={0}
      title="Open dier"
      onClick={onDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onDetails();
        }
      }}
    >
      <div className="status-card__media">
        {card.verblijfNummer ? <span className="status-card__id">{card.verblijfNummer}</span> : null}
        {card.pleeggezin ? <span className="status-card__foster-badge">🏠 {fosterLabel}</span> : null}
        {photoUrl ? (
          // The uploaded photo, fetched authenticated as a blob → object URL (see useAnimalPhotoUrls);
          // falls back to the feed's online `fotoUrl`. A plain <img src> can't carry the bearer, and the
          // Jmix-only /dier-foto path 404s here, so cards went photo-less before this.
          <img className="status-card__image" src={photoUrl} alt={card.naam ?? ""} />
        ) : (
          // Source `.fotoPlaceholderImg`: the camera-with-paw illustration, no caption (matches acc).
          <img
            className="status-card__placeholder-img"
            src="/themes/backoffice/images/foto-niet-beschikbaar.png"
            alt=""
            aria-hidden="true"
          />
        )}
        <span className={`status-card__status status-card__status--${tone}`}>{statusLabel}</span>
      </div>

      <div className="status-card__body">
        <h3 className="status-card__name">{metNummer(card.registratieNummer, card.naam)}</h3>
        <div className="status-card__breed">{card.ras || card.diersoort || "Onbekend ras"}</div>

        <div className="status-card__meta">
          {rows.map((f) => (
            <div className="status-card__meta-row" key={f.key}>
              <span className="status-card__meta-icon" aria-hidden="true">{FIELD_ICON[f.key]}</span>
              <span className="status-card__meta-label">{metaLabel(f.key)}:</span>
              {f.verval ? (
                <span className={`status-card__meta-value status-card__verval status-card__verval--${vervalTone}`}>{vervalText}</span>
              ) : (
                <span className="status-card__meta-value">{f.get(card) || "—"}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="status-card__footer">
        <div className="status-card__chip">
          <span className="status-card__chip-label">
            <MicrochipIcon width={14} height={14} /> {chipLabel}
          </span>
          {card.chipnummer ? (
            <a
              className="status-card__chip-value status-card__chip-link"
              href={`https://www.petbase.eu/chipnummer-zoeken/?chip_number=${encodeURIComponent(card.chipnummer)}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {card.chipnummer}
            </a>
          ) : (
            <span className="status-card__chip-value">—</span>
          )}
        </div>
        <div className="status-card__actions">
          {/* Source parity: only the pencil ("Enting toevoegen") lives on the card — no invented ⚙
              "Instellingen (producten)" button, and no "Details" button (card click opens the animal). */}
          <button
            type="button"
            className="status-card__iconbtn"
            title={bewerkenTitle}
            aria-label={bewerkenTitle}
            onClick={(e) => {
              e.stopPropagation();
              onBewerken();
            }}
          >
            <PencilIcon width={16} height={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** The list-view row (source `DierRij`): status pill + animal cell + the chosen field columns + actions. */
